/**
 * 「提案」のカード（ADR-0060、Phase 3-7）の中身。DOM を持たない部分（テストできるように分ける）。
 * 何を出すかは Gateway が DB の行と設定からコードで作った値（GET /v1/proposals）だけで決める。
 * Jarvis の説明（何を・なぜ・危険・戻し方・申請の根拠）は LLM の文なので、事実（テストの結果・差分・実績・コマンド）と分けて出す。
 */
export interface CapabilityDefView {
  scope: string;
  risk: string;
  approval: string;
}

export interface TrackRecordView {
  since: string;
  proposed: number;
  approved: number;
  rejected: number;
  expired: number;
  executed: number;
  failed: number;
}

export interface PermissionCardView {
  id: string;
  capability: string;
  change: "add" | "modify" | "remove";
  current: CapabilityDefView | null;
  requested: CapabilityDefView | null;
  patch_path: string;
  from_version: number;
  to_version: number;
  apply_command: string;
  applied: boolean;
  why: string;
  risk: string;
  undo: string;
  trace_id: string;
  track_record: TrackRecordView;
  status: "proposed" | "accepted" | "rejected" | "suppressed";
  status_label: string;
  decided_at: string | null;
}

export interface ProposalCardView {
  id: string;
  title: string;
  kind: string;
  status: "proposed" | "approved" | "rejected" | "fix_requested" | "superseded";
  status_label: string;
  created_at: string;
  trace_id: string;
  job_id: string;
  branch: string;
  head_sha: string;
  release_command: string;
  what: string;
  why: string;
  risks: string;
  undo: string;
  test_result: {
    command: string;
    passed: boolean;
    protected_untouched: boolean;
    fix_rounds: number;
    permission_patches_ok: boolean | null;
  };
  diff: {
    files: Array<{ path: string; added: number | null; deleted: number | null }>;
    more_files: number;
    added: number;
    deleted: number;
  };
  protected_drafts: string[];
  permissions: PermissionCardView[];
  decided_at: string | null;
  decision_comment: string | null;
  follow_up: { job_id: string; status_label: string } | null;
}

export interface ProposalsView {
  proposals: ProposalCardView[];
  open: number;
  max_open: number;
  frozen: boolean;
}

/** 本人の答え待ち（提案と、権限の申請は別に数える） */
export function waiting(v: ProposalsView): { proposals: number; permissions: number } {
  return {
    proposals: v.proposals.filter((p) => p.status === "proposed").length,
    permissions: v.proposals.flatMap((p) => p.permissions).filter((r) => r.status === "proposed")
      .length,
  };
}

/** 会話の中（琥珀のカード）に出すもの: 答え待ちの提案か、答え待ちの申請がある提案 */
export function inlineProposals(v: ProposalsView): ProposalCardView[] {
  return v.proposals.filter(
    (p) => p.status === "proposed" || p.permissions.some((r) => r.status === "proposed"),
  );
}

/** テストの結果の 1 行（作業係のコードが作った事実から） */
export function testLine(t: ProposalCardView["test_result"]): string {
  const parts = [
    `${t.command} ${t.passed ? "通過" : "不通過"}`,
    t.protected_untouched ? "保護対象 触れていない" : "保護対象に触れた",
    `直し ${t.fix_rounds} 回`,
  ];
  if (t.permission_patches_ok !== null)
    parts.push(t.permission_patches_ok ? "権限の差分 当たる・版 +1" : "権限の差分 不通過");
  return parts.join("・");
}

/** 差分の要約の 1 行 */
export function diffLine(d: ProposalCardView["diff"]): string {
  const n = d.files.length + d.more_files;
  return `${n} ファイル +${d.added} −${d.deleted}`;
}

const SCOPE: Record<string, string> = { read: "読む", write: "書く", irreversible: "戻せない操作" };
const APPROVAL: Record<string, string> = {
  none: "承認なし",
  first_time: "初回だけ承認",
  always: "毎回承認",
  always_with_identity: "毎回承認＋本人確認",
};

export function defLine(d: CapabilityDefView | null): string {
  if (!d) return "なし";
  return `${SCOPE[d.scope] ?? d.scope}・危険度 ${d.risk}・${APPROVAL[d.approval] ?? d.approval}`;
}

/** 申請の中身の 1 行（今 → 申請） */
export function changeLine(r: PermissionCardView): string {
  if (r.change === "add") return `足す: ${defLine(r.requested)}`;
  if (r.change === "remove") return `外す（今: ${defLine(r.current)}）`;
  return `変える: ${defLine(r.current)} → ${defLine(r.requested)}`;
}

/** 実績の 1 行（承認の記録から Gateway が数えた数） */
export function trackLine(t: TrackRecordView): string {
  const since = t.since.slice(0, 10);
  if (t.proposed === 0) return `${since} から: この能力の承認の記録はまだありません`;
  return `${since} から: 承認の依頼 ${t.proposed} 件（承認 ${t.approved}・却下 ${t.rejected}・期限切れ ${t.expired}）、実行 ${t.executed}・失敗 ${t.failed}`;
}

/** 答えた後の 1 行（本人がすること） */
export function nextStep(p: ProposalCardView): string | null {
  if (p.status === "approved") {
    const perms = p.permissions.filter((r) => r.status === "accepted" && !r.applied);
    return perms.length > 0
      ? `MacBook で ${p.release_command} → ${perms.map((r) => r.apply_command).join(" → ")}`
      : `MacBook で ${p.release_command}`;
  }
  if (p.status === "fix_requested")
    return p.follow_up
      ? `直してもらっています（作業係の仕事: ${p.follow_up.status_label}）`
      : "直してもらっています";
  return null;
}

export const DECIDE_ERROR_TEXT: Record<string, string> = {
  already_decided: "もう答えてあります。",
  frozen: "凍結中のため、今は答えられません（却下はできます）。",
  comment_required: "直してほしいことを書いてください。",
  passkey_session_required: "パスキーで登録した端末からだけ答えられます。",
  voice_device_not_allowed: "声の端末からは答えられません。",
  not_found: "この提案は見つかりません。",
};
